import { Button, Modal } from '@arcflight/tf-component-library';
import moment from 'moment';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { DashboardState } from '../../models';
import { Version } from '../../models/versions';

interface TablePopUpProps {
  visible: boolean;
  data: Version;
  handleClose: () => void;
}

const ModalHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  div {
    margin-right: 4px;
    color: #242d41;
    margin-bottom: 32px;
  }
`;

const HeavyFont = styled.div`
  font-weight: 500;
`;

const BoxTitle = styled.div`
  text-transform: capitalize;
  color: rgba(36, 45, 65, 0.6);
`;

const ChangesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  row-gap: 20px;
  color: rgba(36, 45, 65, 0.6);
  padding: 12px 0;
  border-top: 1px solid rgba(36, 45, 65, 0.05);
`;

const HashChangesWrapper = styled.div`
  border-left: 2px solid rgba(36, 45, 65, 0.1);
  margin-left: 6px;
`;

const HashChangesRow = styled(ChangesRow)`
  border-top: 1px solid rgba(36, 45, 65, 0.1);
  padding-left: 24px;
`;

const Header = styled.div`
  margin-bottom: 4px;
  font-weight: 500;
`;

const FromRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding-bottom: 4px;
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;
`;

const ScrollableDiv = styled.div`
  overflow: auto;
`;

const EmptyValue = styled.span`
  font-style: italic;
`;

const MissingValue = styled.span`
  font-style: italic;
`;

const displayValue = (value: unknown): ReactNode => {
  if (Array.isArray(value) && value.length === 0) return <EmptyValue>empty</EmptyValue>;
  if (value == null) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return value.toString();
};

const formatFieldName = (field: string): string => field.replaceAll('_', ' ');

const isHashValue = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const hashKeys = (oldValue: Record<string, unknown>, newValue: Record<string, unknown>): string[] =>
  Array.from(new Set([...Object.keys(oldValue), ...Object.keys(newValue)]));

const displayHashValue = (hash: Record<string, unknown>, key: string): ReactNode => {
  if (!Object.prototype.hasOwnProperty.call(hash, key)) return <MissingValue>missing</MissingValue>;
  return displayValue(hash[key]);
};

const TablePopUp: React.FC<TablePopUpProps> = ({ visible, data, handleClose }) => {
  const { dateFormat } = useSelector((state: DashboardState) => state.userSettings);

  const { formatMessage } = useIntl();

  const displayData = (): ReactNode => {
    if (!data?.changeset) return null;

    return data.changeset.map((entry) => {
      if (isHashValue(entry.old) && isHashValue(entry.new)) {
        const oldHash = entry.old;
        const newHash = entry.new;

        return (
          <React.Fragment key={entry.field}>
            <ChangesRow data-testid={`TablePopUp--ChangesRow-${entry.field}`}>
              <BoxTitle data-testid={`TablePopUp--Title-${entry.field}`}>{formatFieldName(entry.field)}</BoxTitle>
              <div data-testid={`TablePopUp--Old-${entry.field}`} />
              <div data-testid={`TablePopUp--Updated-${entry.field}`} />
            </ChangesRow>
            <HashChangesWrapper>
              {hashKeys(oldHash, newHash).map((key) => (
                <HashChangesRow key={key} data-testid={`TablePopUp--HashRow-${entry.field}-${key}`}>
                  <BoxTitle data-testid={`TablePopUp--HashTitle-${entry.field}-${key}`}>
                    {formatFieldName(key)}
                  </BoxTitle>
                  <div data-testid={`TablePopUp--HashOld-${entry.field}-${key}`}>
                    {displayHashValue(oldHash, key)}
                  </div>
                  <div data-testid={`TablePopUp--HashUpdated-${entry.field}-${key}`}>
                    {displayHashValue(newHash, key)}
                  </div>
                </HashChangesRow>
              ))}
            </HashChangesWrapper>
          </React.Fragment>
        );
      }

      return (
        <ChangesRow key={entry.field} data-testid={`TablePopUp--ChangesRow-${entry.field}`}>
          <BoxTitle data-testid={`TablePopUp--Title-${entry.field}`}>{formatFieldName(entry.field)}</BoxTitle>
          <div data-testid={`TablePopUp--Old-${entry.field}`}>{displayValue(entry.old)}</div>
          <div data-testid={`TablePopUp--Updated-${entry.field}`}>{displayValue(entry.new)}</div>
        </ChangesRow>
      );
    });
  };

  const Title = data?.justification
    ? data?.justification.charAt(0).toUpperCase() + data?.justification.slice(1)
    : 'History';

  return (
    <div data-testid="TablePopUp--ModalWrapper">
      <Modal isOpen={visible} handleClose={handleClose}>
        <ScrollableDiv>
          <ModalHeader data-testid="TablePopUp--ModalHeader">
            <HeavyFont>{Title}:</HeavyFont>
            <div>{moment.utc(data?.timestamp).format(`${dateFormat} HH:mmz`)}</div>
          </ModalHeader>
          <FromRow data-testid="TablePopUp--FromRow">
            <div />
            <Header data-testid="TablePopUp--FromHeader">{formatMessage({ id: 'text.from' })}</Header>
            <Header data-testid="TablePopUp--ToHeader">{formatMessage({ id: 'text.to' })}</Header>
          </FromRow>
          {displayData()}
          <ButtonWrapper data-testid="TablePopUp--ButtonWrapper">
            <Button height="32px" onClick={handleClose}>
              {formatMessage({ id: 'text.close' })}
            </Button>
          </ButtonWrapper>
        </ScrollableDiv>
      </Modal>
    </div>
  );
};

export default TablePopUp;
